import { create } from 'zustand';
import { adminAuctionApi } from '@api/api-config.ts';
import {
  AdminAuctionDetailResponse,
  AdminAuctionSupplierResponse,
  AdminAuctionProductResponse,
  AdminAuctionPriceResponse,
  AdminAuctionCustomerResponse,
  PriceType,
} from '@api/generated/portal-be-admin';

interface PaginationState {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface AdminAuctionDetailStoreState {
  // Základní detail
  loading: boolean;
  error: string | null;
  auction: AdminAuctionDetailResponse | null;

  // Dodavatelé
  suppliers: AdminAuctionSupplierResponse[];
  suppliersLoading: boolean;
  suppliersError: string | null;
  suppliersPagination: PaginationState | null;

  // Produkty
  products: AdminAuctionProductResponse[];
  productsLoading: boolean;
  productsError: string | null;
  productsPagination: PaginationState | null;

  // Ceny
  prices: AdminAuctionPriceResponse[];
  pricesLoading: boolean;
  pricesError: string | null;
  pricesPagination: PaginationState | null;

  // Zákazníci
  customers: AdminAuctionCustomerResponse[];
  customersLoading: boolean;
  customersError: string | null;
  customersPagination: PaginationState | null;

  // Akce
  loadAuctionDetail: (auctionId: number) => Promise<void>;
  loadSuppliers: (auctionId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  loadProducts: (auctionId: number, params: { page?: number; size?: number; q?: string; supplierId?: number }) => Promise<void>;
  loadPrices: (
    auctionId: number,
    params: { page?: number; size?: number; supplierId?: number; customerId?: number; productQuery?: string; priceType?: PriceType; guaranteed?: boolean },
  ) => Promise<void>;
  loadCustomers: (auctionId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  reset: () => void;
}

export const useAdminAuctionDetailStore = create<AdminAuctionDetailStoreState>(set => ({
  // Základní detail
  loading: false,
  error: null,
  auction: null,

  // Dodavatelé
  suppliers: [],
  suppliersLoading: false,
  suppliersError: null,
  suppliersPagination: null,

  // Produkty
  products: [],
  productsLoading: false,
  productsError: null,
  productsPagination: null,

  // Ceny
  prices: [],
  pricesLoading: false,
  pricesError: null,
  pricesPagination: null,

  // Zákazníci
  customers: [],
  customersLoading: false,
  customersError: null,
  customersPagination: null,

  loadAuctionDetail: async (auctionId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await adminAuctionApi.getAuctionDetail({ auctionId });
      set({
        loading: false,
        error: null,
        auction: response.data,
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba při načítání detailu aukce',
        auction: null,
      });
    }
  },

  loadSuppliers: async (auctionId: number, params) => {
    try {
      set({ suppliersLoading: true, suppliersError: null });
      const response = await adminAuctionApi.getAuctionSuppliers({
        auctionId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
      });
      set({
        suppliersLoading: false,
        suppliersError: null,
        suppliers: response.data.items,
        suppliersPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        suppliersLoading: false,
        suppliersError: error instanceof Error ? error.message : 'Chyba při načítání dodavatelů',
        suppliers: [],
        suppliersPagination: null,
      });
    }
  },

  loadProducts: async (auctionId: number, params) => {
    try {
      set({ productsLoading: true, productsError: null });
      const response = await adminAuctionApi.getAuctionProducts({
        auctionId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
        supplierId: params.supplierId,
      });
      set({
        productsLoading: false,
        productsError: null,
        products: response.data.items,
        productsPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        productsLoading: false,
        productsError: error instanceof Error ? error.message : 'Chyba při načítání produktů',
        products: [],
        productsPagination: null,
      });
    }
  },

  loadPrices: async (auctionId: number, params) => {
    try {
      set({ pricesLoading: true, pricesError: null });
      const response = await adminAuctionApi.getAuctionPrices({
        auctionId,
        page: params.page || 1,
        size: params.size || 20,
        supplierId: params.supplierId,
        customerId: params.customerId,
        productQuery: params.productQuery,
        priceType: params.priceType,
        isGuaranteed: params.guaranteed,
      });
      set({
        pricesLoading: false,
        pricesError: null,
        prices: response.data.items,
        pricesPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        pricesLoading: false,
        pricesError: error instanceof Error ? error.message : 'Chyba při načítání cen',
        prices: [],
        pricesPagination: null,
      });
    }
  },

  loadCustomers: async (auctionId: number, params) => {
    try {
      set({ customersLoading: true, customersError: null });
      const response = await adminAuctionApi.getAuctionCustomers({
        auctionId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
      });
      set({
        customersLoading: false,
        customersError: null,
        customers: response.data.items,
        customersPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        customersLoading: false,
        customersError: error instanceof Error ? error.message : 'Chyba při načítání zákazníků',
        customers: [],
        customersPagination: null,
      });
    }
  },

  reset: () => {
    set({
      loading: false,
      error: null,
      auction: null,
      suppliers: [],
      suppliersLoading: false,
      suppliersError: null,
      suppliersPagination: null,
      products: [],
      productsLoading: false,
      productsError: null,
      productsPagination: null,
      prices: [],
      pricesLoading: false,
      pricesError: null,
      pricesPagination: null,
      customers: [],
      customersLoading: false,
      customersError: null,
      customersPagination: null,
    });
  },
}));
